/**
 * How the transcript fold turns a `suggest_environment` call into its card.
 * Only a saved message carries the suggestion: live events cut long strings
 * and hide what looks like a secret, so a live call's card waits for the saved
 * copy. Like a question, the card is not a trace step.
 */
import type { SessionMessageAssistant } from '@opencode/client/promise';

import type { SessionPart } from './transcript-model';

import { parseEnvironmentSuggestion } from '@/lib/code-sessions/environment-suggestion';

type ToolContent = Extract<SessionMessageAssistant['content'][number], { type: 'tool' }>;

export type EnvironmentSuggestionPart = Extract<SessionPart, { type: 'environment_suggestion' }>;

/**
 * A saved `suggest_environment` call as its card. An errored call shows
 * nothing: OpenCode hands the error back to the model, which calls again. A
 * streaming call's input is partial JSON text, so its card waits for the input.
 * The saved transcript is never cut short; a secret it hides keeps the card
 * from saving (`applyEnvironmentSuggestion`).
 */
export function savedEnvironmentSuggestionPart(
  content: ToolContent,
  completed: boolean,
): EnvironmentSuggestionPart | undefined {
  if (content.state.status === 'error') {
    return undefined;
  }
  const running =
    !completed && (content.state.status === 'running' || content.state.status === 'streaming');
  const suggestion =
    content.state.status === 'streaming' ? null : parseEnvironmentSuggestion(content.state.input);
  if (suggestion === null && !running) {
    return undefined;
  }
  return {
    type: 'environment_suggestion',
    callId: content.id,
    suggestion,
    running: running ? true : undefined,
  };
}

/**
 * A live call's card from its `tool.input.started` event. It has no
 * suggestion until the saved message replaces it.
 */
export function liveEnvironmentSuggestionPart(callId: string): EnvironmentSuggestionPart {
  return { type: 'environment_suggestion', callId, suggestion: null, running: true };
}

/**
 * A live call's later events: it stops running when it succeeds, and a
 * failed call leaves the transcript, since the model sees the error and calls again.
 */
export function foldEnvironmentSuggestionEvent(
  parts: SessionPart[],
  part: EnvironmentSuggestionPart,
  type: string,
): void {
  switch (type) {
    case 'session.tool.success':
      part.running = undefined;
      return;
    case 'session.tool.failed': {
      const index = parts.indexOf(part);
      if (index !== -1) {
        parts.splice(index, 1);
      }
    }
  }
}
